
import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DownstreamMetricsFilteredByFieldQuery,
  DownstreamMetricsFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/DownstreamMetricsFilteredByFieldQuery';
import {
  DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQuery,
  DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQuery';
import { MetricsDisplayGridRowDataFragment } from '../../../gen-src/queries/MetricsDisplayGridRowDataFragment';
import { MetricsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/MetricsDisplayGridRowDataFragmentWithoutPersonalSpace';
import {
  WorkbookDownstreamMetricsQuery,
  WorkbookDownstreamMetricsQueryVariables,
} from '../../../gen-src/queries/WorkbookDownstreamMetricsQuery';
import {
  WorkbookDownstreamMetricsWithoutPersonalSpaceQuery,
  WorkbookDownstreamMetricsWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/WorkbookDownstreamMetricsWithoutPersonalSpaceQuery';
import { MetricSortOrder } from '../../../gen-src/queries/globalTypes';
import { DownstreamMetricsFilteredByFieldQueryGql } from '../../queries/DownstreamMetricsFilteredByFieldQueryGql';
import { DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQueryGql } from '../../queries/DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQueryGql';
import { WorkbookDownstreamMetricsQueryGql } from '../../queries/WorkbookDownstreamMetricsQueryGql';
import { WorkbookDownstreamMetricsWithoutPersonalSpaceQueryGql } from '../../queries/WorkbookDownstreamMetricsWithoutPersonalSpaceQueryGql';
import { getRowDataForMetric, getRowDataForMetricWithoutPersonalSpace, MetricsDisplayGrid, MetricsDisplayGridRowData } from '../sharedDisplayGrid/MetricsDisplayGrid';
import { MetricsValidSortFields } from '../sorting/sortProviders/MetricsGridSortProvider';

type QueryVariables = WorkbookDownstreamMetricsQueryVariables;
type QueryResults = WorkbookDownstreamMetricsQuery;
type QueryResultListNode = MetricsDisplayGridRowDataFragment;
type GridRowData = MetricsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = WorkbookDownstreamMetricsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = WorkbookDownstreamMetricsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = MetricsDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = DownstreamMetricsFilteredByFieldQueryVariables;
type QueryResultsWithFilter = DownstreamMetricsFilteredByFieldQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData?.workbooks[0]?.downstreamMetricsConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData?.workbooks[0]?.downstreamMetricsConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData?.fieldsConnection?.nodes[0]?.downstreamMetricsConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(resultData: QueryResultsWithFilterWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData?.fieldsConnection?.nodes[0]?.downstreamMetricsConnection;
}

export type WorkbookDownstreamMetricsQueryGridProps =
  GridSortProps<MetricsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredFieldId: string | null;
    readonly rowCount: number | null;
  };

export function WorkbookDownstreamMetricsQueryGrid(props: WorkbookDownstreamMetricsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamMetricsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredMetricsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFieldId ? (
      // Query with Selected Field as Filter
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={DownstreamMetricsFilteredByFieldQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as MetricSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForMetric}
      >
        {({ rowStore }) => <MetricsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        // Query without Selection
        <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
          queryGql={WorkbookDownstreamMetricsQueryGql}
          partialQueryVariables={{ workbookVizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as MetricSortOrder }}
          selectConnectionListResult={selectConnectionListResult}
          mapResultNode={getRowDataForMetric}
        >
          {({ rowStore }) => <MetricsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as MetricSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForMetricWithoutPersonalSpace}
      >
        {({ rowStore }) => <MetricsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
        QueryResultListNodeWithoutPersonalSpace, GridRowData>
          queryGql={WorkbookDownstreamMetricsWithoutPersonalSpaceQueryGql}
          partialQueryVariables={{ workbookVizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as MetricSortOrder }}
          selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
          mapResultNode={getRowDataForMetricWithoutPersonalSpace}
        >
          {({ rowStore }) => <MetricsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  );
}
